import { Vector2, Vector3 } from 'three';

/**
 * The last pass: everything that belongs to the eye rather than to the city.
 *
 * - a radial chromatic smear, because old glass is never quite corrected
 * - the telescope iris, which closes the frame to a circle when the traveller
 *   lifts an eyepiece, and brings its own ring of ground-glass dust with it
 * - a vignette that deepens as the evening does
 * - film grain, so the darkness is never flat
 * - a memory bleed: a soft, slightly displaced warm ghost of the frame, which
 *   grows through the descent until the image is half remembering itself
 */
export const CompositeShader = {
  name: 'CompositeShader',

  uniforms: {
    tDiffuse: { value: null },
    uTime: { value: 0 },
    uResolution: { value: new Vector2(1, 1) },
    uVignette: { value: 0.86 },
    uGrain: { value: 0.055 },
    uAberration: { value: 0.0016 },
    uIris: { value: 0 },
    uBleed: { value: 0 },
    uTint: { value: new Vector3(0.07, 0.09, 0.16) },
    uFlash: { value: 0 },
  },

  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: /* glsl */ `
    precision highp float;

    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform vec2  uResolution;
    uniform float uVignette;
    uniform float uGrain;
    uniform float uAberration;
    uniform float uIris;
    uniform float uBleed;
    uniform vec3  uTint;
    uniform float uFlash;

    varying vec2 vUv;

    float hash(vec2 p) {
      p = fract(p * vec2(443.8975, 397.2973));
      p += dot(p, p.yx + 19.19);
      return fract((p.x + p.y) * p.x);
    }

    void main() {
      vec2 uv = vUv;
      vec2 centred = uv - 0.5;
      centred.x *= uResolution.x / max(uResolution.y, 1.0);
      float r = length(centred);

      // ── chromatic aberration, scaled by distance from the axis ───────────
      float amount = uAberration * (1.0 + uIris * 3.5) * (0.25 + r * r * 3.0);
      vec2 dir = r > 1e-5 ? centred / r : vec2(0.0);
      vec3 col;
      col.r = texture2D(tDiffuse, uv - dir * amount).r;
      col.g = texture2D(tDiffuse, uv).g;
      col.b = texture2D(tDiffuse, uv + dir * amount).b;

      // ── memory bleed: a warm, lagging ghost of the frame ─────────────────
      if (uBleed > 0.001) {
        vec2 off = dir * (0.004 + 0.012 * uBleed) + vec2(0.0, 0.0015 * uBleed);
        vec3 ghost = texture2D(tDiffuse, uv - off).rgb;
        ghost *= vec3(1.12, 0.96, 0.78);
        col = mix(col, max(col, ghost * 0.92), uBleed * 0.55);
      }

      // ── the telescope iris ───────────────────────────────────────────────
      if (uIris > 0.001) {
        float aspect = uResolution.x / max(uResolution.y, 1.0);
        float radius = mix(1.6, 0.345, uIris);
        float edge = mix(0.9, 0.055, uIris);
        float inside = 1.0 - smoothstep(radius - edge, radius, r);

        // Ground glass: dust and a faint coating rainbow on the field stop.
        float ring = smoothstep(radius - edge * 1.6, radius - edge * 0.2, r)
                   * (1.0 - smoothstep(radius - edge * 0.2, radius, r));
        vec3 coating = vec3(0.45, 0.62, 1.0) * ring * 0.22 * uIris;

        // A few motes on the field lens. Rare, or it reads as a broken shader.
        float dust = hash(floor(uv * uResolution * 0.22) + 3.1);
        dust = smoothstep(0.9991, 1.0, dust) * inside * uIris * 0.35;

        col = col * mix(1.0, inside, uIris) + coating + dust;

        // A very slight barrel darkening inside the field.
        col *= 1.0 - uIris * 0.22 * smoothstep(0.0, radius, r);
        aspect; // keep the symbol used on drivers that fold the branch
      }

      // ── vignette and the colour of the hour at the corners ───────────────
      float vig = 1.0 - uVignette * smoothstep(0.26, 0.98, r);
      col = mix(col, col * vig + uTint * (1.0 - vig) * 0.5, 0.92);

      // ── the flash of arrival ─────────────────────────────────────────────
      col += uFlash * vec3(1.0, 0.86, 0.66);

      // ── grain ────────────────────────────────────────────────────────────
      // Strongest in the midtones, the way film is; the shadows of a city at
      // dusk should be quiet, not noisy.
      float luma = dot(col, vec3(0.2126, 0.7152, 0.0722));
      float g = hash(uv * uResolution + fract(uTime) * 313.7) - 0.5;
      col += g * uGrain * smoothstep(0.0, 0.22, luma) * (1.0 - smoothstep(0.55, 1.0, luma));

      gl_FragColor = vec4(max(col, 0.0), 1.0);
    }
  `,
};
